import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Box, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  CONTRACT_RENEWAL_STEPS,
  ContractRenewalStatusDto,
  ZONE_KEYS,
  useProjectSecurityContext,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import { Button, Loading } from 'framework/ui/mui-builder';
import ContractRenewalActions from '../shared/ContractRenewalActions';
import { getCommercialRequiredActionsHealthchecks } from './utils';
import { RequiredActionItem } from './sections/RequiredActionsSection';
import { CommercialStateReviewAction, CommercialStateUpdateAction } from './CommercialStateActions';
import { ControlData } from '../types';
import { ASSUMPTION_STATUS } from '../../assumptions/types';

type CommercialRequiredActionsProps = {
  data: ControlData;
  refetchControlsData: () => Promise<unknown>;
  renewalStatus: ContractRenewalStatusDto | null;
  isRenewalLoading: boolean;
  contractRenewalMessage: string | null;
  onContractRenewalConfirm: () => void;
};

export const useCommercialRequiredActions = ({
  data,
  refetchControlsData,
  renewalStatus,
  isRenewalLoading,
  contractRenewalMessage,
  onContractRenewalConfirm,
}: CommercialRequiredActionsProps): RequiredActionItem[] => {
  const navigate = useNavigate();
  const { getAssumptionsUrl } = useNewProjectUrl();
  const { isAccessGranted } = useProjectSecurityContext();

  return useMemo(() => {
    const allAssumptions = data?.assumptions || [];
    const hasAssumptionsRequiringVerification = allAssumptions.some(
      assumption => !!assumption.isVerificationRequired && assumption.status !== ASSUMPTION_STATUS.INACTIVE,
    );
    const { commercialStateHealthcheck, assumptionsHealthcheck, contractRenewalHealthcheck, budgetStateHealthcheck } =
      getCommercialRequiredActionsHealthchecks(data);
    const canManageCommercialState = isAccessGranted(
      ACCESS_INFO_SECTIONS.PROJECT_DIMENSION,
      ACCESS_INFO_ACTIONS.MANAGE,
    );
    const canViewContractRenewal = isAccessGranted(ACCESS_INFO_SECTIONS.CONTRACT_RENEWAL, ACCESS_INFO_ACTIONS.VIEW);
    const canViewAssumptions = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.VIEW);

    const actions: RequiredActionItem[] = [];
    if (
      canViewContractRenewal &&
      (contractRenewalHealthcheck || budgetStateHealthcheck) &&
      (isRenewalLoading ||
        (renewalStatus && renewalStatus.step !== CONTRACT_RENEWAL_STEPS.NO_ACTION_REQUIRED && contractRenewalMessage))
    ) {
      actions.push({
        id: 'contract-renewal',
        message: '',
        actions: isRenewalLoading ? (
          <Loading isInitialPosition />
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
              <WarningAmberOutlined color="error" fontSize="small" />
              <Typography variant="body1">{contractRenewalMessage}</Typography>
            </Stack>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
              }}
            >
              <ContractRenewalActions
                renewalStatus={renewalStatus}
                onConfirm={onContractRenewalConfirm}
                project={data}
                sxProps={{
                  flexDirection: { xs: 'column', lg: 'column' },
                  '@media (min-width:1680px)': {
                    flexDirection: 'row',
                  },
                }}
              />
            </Box>
          </Stack>
        ),
      });
    }

    if (canManageCommercialState && commercialStateHealthcheck && !data?.projectDimension?.commercialState) {
      actions.push({
        id: 'commercial-state-not-set',
        message: 'Commercial state is not defined.',
        actions: <CommercialStateUpdateAction data={data} refetchControlsData={refetchControlsData} />,
      });
    }

    if (
      canManageCommercialState &&
      data?.projectDimension?.commercialState &&
      commercialStateHealthcheck &&
      commercialStateHealthcheck.targetZone !== ZONE_KEYS.SAFE
    ) {
      actions.push({
        id: 'commercial-state-review',
        message: 'Commercial state has not been confirmed more then 1 month.',
        actions: <CommercialStateReviewAction data={data} refetchControlsData={refetchControlsData} />,
      });
    }

    if (canViewAssumptions && assumptionsHealthcheck && allAssumptions.length === 0) {
      actions.push({
        id: 'assumptions-not-defined',
        message: 'Assumptions are not defined.',
        actions: (
          <Button variant="contained" onClick={() => navigate(getAssumptionsUrl(data.id))}>
            Define
          </Button>
        ),
      });
    } else if (
      canViewAssumptions &&
      assumptionsHealthcheck &&
      hasAssumptionsRequiringVerification &&
      assumptionsHealthcheck.targetZone === ZONE_KEYS.MONITORING
    ) {
      actions.push({
        id: 'assumptions-review',
        message: 'Assumptions have not been confirmed recently.',
        actions: (
          <Button variant="contained" onClick={() => navigate(getAssumptionsUrl(data.id))}>
            Review assumptions
          </Button>
        ),
      });
    }

    return actions;
  }, [
    contractRenewalMessage,
    data,
    getAssumptionsUrl,
    isAccessGranted,
    isRenewalLoading,
    navigate,
    onContractRenewalConfirm,
    refetchControlsData,
    renewalStatus,
  ]);
};
